import { useMemo, useState } from "react";
import type { CaseRecord, RecordFact } from "../../types";
import { displayFactTitle, formatFactDate } from "../../lib/factMeta";
import {
  buildFinancialGroups,
  type FinancialLine,
  type RecordFinancialGroup,
} from "../../lib/financialFlow";
import { formatCents } from "../../lib/costsMath";
import {
  BUCKET_LABELS,
  bucketOf,
  categoryFactKey,
  computeOverage,
  discrepancyRecordIds,
  type CaseTotals,
  type CategoryOverrides,
  type LedgerBucket,
  type LedgerDisposition,
  type LedgerItem,
} from "../../lib/financialTotals";
import { buildLedger, type StatusOverride } from "../../lib/ledger";
import { buildCostsByContext } from "../../lib/costsByContext";
import { scanForExtractionGaps } from "../../lib/completeness";
import CostsByContext from "./CostsByContext";
import FactProvenance from "./FactProvenance";
import { AiMark, VerifiedMark } from "./ProvenanceMark";

// Capped preview inside the (already collapsed) section — long record lists
// show the first few groups + "view all N" (Round 13, Phase 1, link-don't-dump).
const GROUP_PREVIEW = 6;

// Mechanical financial summary (Round 12, restructured Round 13 Financial
// worklist). All deterministic integer-cent / exact-fold math over already
// VERIFIED facts — no AI in the number path. The surfaces, top to bottom:
//   1. CONFIRM WORKLIST (primary): one row per amount still awaiting a type
//      decision — source record + extracted sentence + value + the 4-type
//      dropdown, expandable in place for source context. Working through it to
//      zero unlocks the case total.
//   2. EXCLUDED-FROM-TOTAL inspector: every amount left OUT of the net — by the
//      automatic per-record fold (a component of a stated total) or by the user
//      ("already counted") — shown with its reason and a one-click undo. Nothing
//      leaves the total silently.
//   3. CASE TOTAL: asserted (Σcost − Σrecovery) once every cash amount is
//      confirmed; otherwise withheld with the worklist as the ask.
//   4. COSTS vs COVERAGE: a demoted comparison of total costs against the
//      coverage-term subtotal — only when both exist.
//   5. Per-record breakdown + completeness callout (unchanged base layer).

const BUCKETS: LedgerBucket[] = ["cost", "recovery", "coverage_term", "rate"];

// Source context joined onto a worklist row: the per-record line carries the
// fold components, cross-record corroboration, and reconciliation state.
type LineContext = { line: FinancialLine; group: RecordFinancialGroup };

export default function FinancialSummary({
  facts,
  recordById,
  onNavigateToRecord,
  overrides,
  statusOverrides,
  mergeLinks,
  onSetBucket,
}: {
  facts: RecordFact[];
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
  // Amount-category overrides are owned by the parent (Round 13) — one source
  // of truth shared with the collapsed summary row. Setting a disposition (a
  // type bucket, "exclude", or null to clear/undo) persists via the parent.
  overrides: CategoryOverrides;
  // Status + amount + merge inputs to the SHARED ledger (Round 14) so this net
  // is the SAME computation as the Summary tab — same dedup, status filter,
  // amount overrides. Without these the surfaces would diverge again.
  statusOverrides: ReadonlyMap<string, StatusOverride>;
  mergeLinks: ReadonlyMap<string, "merge" | "separate">;
  onSetBucket: (fact: RecordFact, bucket: LedgerDisposition | null) => void;
}) {
  const [showAllGroups, setShowAllGroups] = useState(false);

  const groups = useMemo(
    () => buildFinancialGroups(facts, recordById),
    [facts, recordById],
  );
  // THE shared ledger (cross-record dedup + status filter + amount overrides).
  const ledger = useMemo(
    () => buildLedger(facts, { bucketOverrides: overrides, statusOverrides, mergeLinks }),
    [facts, overrides, statusOverrides, mergeLinks],
  );
  // A CaseTotals-shaped presentation of the ledger so the existing render +
  // worklist + CostsVsCoverage consume it unchanged — but the NUMBERS now come
  // from the one shared layer, equal to the Summary tab by construction.
  const totals: CaseTotals = useMemo(
    () => ({
      items: ledger.items,
      excluded: ledger.excluded,
      asserted: ledger.net.asserted,
      reason: ledger.net.asserted ? null : ledger.net.reason,
      totals: ledger.net.asserted
        ? {
            costCents: ledger.net.costCents,
            recoveryCents: ledger.net.recoveryCents,
            netCents: ledger.net.netCents,
            bucketSubtotals: ledger.net.bucketSubtotals,
          }
        : null,
      toConfirm: ledger.toConfirm,
    }),
    [ledger],
  );
  // The deterministic by-type breakdown — same shared ledger, one formula.
  const contextTable = useMemo(() => buildCostsByContext(ledger), [ledger]);
  // Per-record discrepancy flags: a stated total that doesn't fold (shared
  // helper so the collapsed summary count and these badges agree).
  const discrepancyRecords = useMemo(
    () => discrepancyRecordIds(facts),
    [facts],
  );
  // factId → its per-record line, for source context on a worklist row.
  const contextByFactId = useMemo(() => {
    const m = new Map<string, LineContext>();
    for (const g of groups) {
      for (const line of g.lines) m.set(line.fact.id, { line, group: g });
    }
    return m;
  }, [groups]);

  const gaps = useMemo(
    () => scanForExtractionGaps([...recordById.values()], facts),
    [recordById, facts],
  );

  const excludedFromTotal = totals.items.filter(
    (i) => i.subsumed || i.excluded,
  );

  const hasAmounts = totals.items.length > 0 || totals.excluded.length > 0;
  if (groups.length === 0 && !hasAmounts && gaps.length === 0) return null;

  const shownGroups = showAllGroups ? groups : groups.slice(0, GROUP_PREVIEW);

  return (
    <>
      {totals.toConfirm.length > 0 && (
        <ConfirmWorklist
          items={totals.toConfirm}
          recordById={recordById}
          contextByFactId={contextByFactId}
          discrepancyRecords={discrepancyRecords}
          overrides={overrides}
          onSetBucket={onSetBucket}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}

      {excludedFromTotal.length > 0 && (
        <ExcludedFromTotal
          items={excludedFromTotal}
          onSetBucket={onSetBucket}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}

      {hasAmounts && (
        <CostsByContext
          table={contextTable}
          recordById={recordById}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}

      <CostsVsCoverage totals={totals} />

      {shownGroups.map((g) => (
        <div className="financial-record-group" key={g.record_id}>
          <h4 className="overview-subsection-title">
            <button
              type="button"
              className="fact-source-link"
              onClick={() => onNavigateToRecord?.(g.record_id)}
            >
              {g.title}
            </button>{" "}
            <span className="overview-muted">{formatFactDate(g.date)}</span>
            {discrepancyRecords.has(g.record_id) && (
              <span
                className="ledger-recon ledger-recon-bad"
                title="A line in this record calls itself a total/net but its components do not add up to it — flagged, not hidden."
              >
                ⚠ doesn’t reconcile
              </span>
            )}
          </h4>
          <div className="table-scroll">
            <table className="fact-table">
              <tbody>
                {g.lines.map((line) => {
                  const f = line.fact;
                  return (
                    <tr key={f.id}>
                      <td>
                        {displayFactTitle(f)} <AiMark what="Line title" />
                        {line.components.length > 0 && (
                          <span
                            className="ledger-recon ledger-recon-ok"
                            title="Itemized components sum EXACTLY to this stated total (integer-cent fold)."
                          >
                            ✓ reconciles
                          </span>
                        )}
                        <FactProvenance
                          recordIds={[f.record_id, ...line.alsoIn]}
                          recordById={recordById}
                          onNavigateToRecord={onNavigateToRecord}
                        />
                        {line.duplicates.length > 0 && (
                          <details className="fact-components">
                            <summary>
                              stated {line.duplicates.length + 1} times in this record
                            </summary>
                            <ul>
                              <li>“{f.verbatim_quote}”</li>
                              {line.duplicates.map((d) => (
                                <li key={d.id}>“{d.verbatim_quote}”</li>
                              ))}
                            </ul>
                          </details>
                        )}
                        {line.components.length > 0 && (
                          <details className="fact-components">
                            <summary>
                              {line.components.length} itemized components
                            </summary>
                            <ul>
                              {line.components.map((c) => (
                                <li key={c.fact.id}>
                                  {displayFactTitle(c.fact)}: {c.fact.amount ?? "—"}
                                  {c.fact.verbatim_quote && (
                                    <div className="overview-muted">
                                      “{c.fact.verbatim_quote}”
                                    </div>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </td>
                      <td className="num">
                        {f.trust_level === "low" ? "≈ " : ""}
                        {f.amount ?? "—"} <VerifiedMark what="Amount" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {groups.length > GROUP_PREVIEW && (
        <button
          type="button"
          className="overview-view-all"
          onClick={() => setShowAllGroups((v) => !v)}
        >
          {showAllGroups
            ? "▴ Show fewer"
            : `▾ View all ${groups.length} records`}
        </button>
      )}

      {gaps.length > 0 && <CompletenessCallout gaps={gaps} onNavigateToRecord={onNavigateToRecord} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// 1. Confirm worklist — the primary Financial surface. One pass, in place.
// ---------------------------------------------------------------------------

function ConfirmWorklist({
  items,
  recordById,
  contextByFactId,
  discrepancyRecords,
  overrides,
  onSetBucket,
  onNavigateToRecord,
}: {
  items: LedgerItem[];
  recordById: Map<string, CaseRecord>;
  contextByFactId: Map<string, LineContext>;
  discrepancyRecords: Set<string>;
  overrides: CategoryOverrides;
  onSetBucket: (fact: RecordFact, bucket: LedgerDisposition | null) => void;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  return (
    <div className="ledger-worklist">
      <div className="ledger-worklist-head">
        <strong>
          Confirm what each amount is — {items.length} left
        </strong>
        <p className="overview-muted">
          Pick the type for each amount so the app can total your case. Click a
          row to see where it came from. Or skip it — every amount is still here
          either way.
        </p>
      </div>
      <ul className="ledger-worklist-rows">
        {items.map((item) => {
          const f = item.fact;
          const rec = recordById.get(f.record_id);
          const ctx = contextByFactId.get(f.id);
          const open = expanded === item.key;
          return (
            <li
              key={item.key}
              className={`ledger-worklist-row${open ? " open" : ""}`}
            >
              <button
                type="button"
                className="ledger-worklist-rowhead"
                aria-expanded={open}
                onClick={() => setExpanded(open ? null : item.key)}
              >
                <span className="ledger-worklist-rectitle">
                  {rec?.subject?.trim() || "Record"}
                </span>
                <span className="ledger-worklist-sentence">{f.summary}</span>
                <span className="num">
                  {f.trust_level === "low" ? "≈ " : ""}
                  {formatCents(item.cents)}
                </span>
                <span className="ledger-worklist-caret">{open ? "▴" : "▾"}</span>
              </button>
              <div className="ledger-worklist-controls">
                <BucketSelect
                  fact={f}
                  overrides={overrides}
                  onChange={(b) => onSetBucket(f, b)}
                />
                <button
                  type="button"
                  className="ledger-worklist-exclude"
                  title="This amount is the same money already counted somewhere else — leave it out of the case total. Reversible."
                  onClick={() => onSetBucket(f, "exclude")}
                >
                  Already counted
                </button>
              </div>
              {open && (
                <SourceContext
                  fact={f}
                  ctx={ctx}
                  hasDiscrepancy={discrepancyRecords.has(f.record_id)}
                  recordById={recordById}
                  onNavigateToRecord={onNavigateToRecord}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// Inline source context for a worklist row — no navigating away. The full
// record is one click away for the cases that need the whole document.
function SourceContext({
  fact,
  ctx,
  hasDiscrepancy,
  recordById,
  onNavigateToRecord,
}: {
  fact: RecordFact;
  ctx: LineContext | undefined;
  hasDiscrepancy: boolean;
  recordById: Map<string, CaseRecord>;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  const line = ctx?.line;
  return (
    <div className="ledger-worklist-context">
      <p className="ledger-context-quote">“{fact.verbatim_quote}”</p>
      <div className="ledger-context-meta">
        {line && line.components.length > 0 && (
          <span className="ledger-recon ledger-recon-ok">
            ✓ reconciles — {line.components.length} itemized component
            {line.components.length === 1 ? "" : "s"}
          </span>
        )}
        {hasDiscrepancy && (
          <span className="ledger-recon ledger-recon-bad">
            ⚠ a stated total in this record doesn’t add up
          </span>
        )}
        {line && line.alsoIn.length > 0 && (
          <span className="ledger-context-corrob">
            Also stated in{" "}
            <FactProvenance
              recordIds={line.alsoIn}
              recordById={recordById}
              onNavigateToRecord={onNavigateToRecord}
            />
          </span>
        )}
      </div>
      {line && line.components.length > 0 && (
        <ul className="ledger-context-components">
          {line.components.map((c) => (
            <li key={c.fact.id}>
              {displayFactTitle(c.fact)}: {c.fact.amount ?? "—"}
            </li>
          ))}
        </ul>
      )}
      <button
        type="button"
        className="fact-source-link ledger-context-open"
        onClick={() => onNavigateToRecord?.(fact.record_id)}
      >
        Open full record →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Excluded-from-total inspector — visible reasons + one-click undo.
// ---------------------------------------------------------------------------

function ExcludedFromTotal({
  items,
  onSetBucket,
  onNavigateToRecord,
}: {
  items: LedgerItem[];
  onSetBucket: (fact: RecordFact, bucket: LedgerDisposition | null) => void;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  return (
    <details className="ledger-excluded-total">
      <summary>
        {items.length} amount{items.length === 1 ? "" : "s"} left out of the
        total — with reasons
      </summary>
      <p className="overview-muted">
        These are kept out of the case total because they’re the same money
        counted somewhere else. Nothing is hidden — undo any of them.
      </p>
      <ul>
        {items.map((item) => {
          const f = item.fact;
          const auto = item.subsumed;
          return (
            <li key={item.key} className="ledger-excluded-row">
              <button
                type="button"
                className="fact-source-link"
                onClick={() => onNavigateToRecord?.(f.record_id)}
              >
                {f.summary}
              </button>{" "}
              <span className="num">{formatCents(item.cents)}</span>{" "}
              <span className="overview-muted">
                {auto
                  ? `auto-excluded — part of ${formatCents(item.subsumedByCents ?? 0)}`
                  : "you marked this already counted"}
              </span>{" "}
              {auto ? (
                <button
                  type="button"
                  className="ledger-undo"
                  title="Count this amount in the total after all — overrides the automatic guess."
                  onClick={() => onSetBucket(f, item.bucket)}
                >
                  Count anyway
                </button>
              ) : (
                <button
                  type="button"
                  className="ledger-undo"
                  title="Undo — put this amount back into the worklist to re-decide."
                  onClick={() => onSetBucket(f, null)}
                >
                  Undo
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

// The 4-type control. AI default until the user confirms/overrides. Choosing
// any value persists it (and counts as "confirmed" for the totals gate).
function BucketSelect({
  fact,
  overrides,
  onChange,
}: {
  fact: RecordFact;
  overrides: CategoryOverrides;
  onChange: (b: LedgerBucket) => void;
}) {
  const current = bucketOf(fact, overrides);
  const confirmed = overrides.has(categoryFactKey(fact));
  return (
    <label className="ledger-bucket-select" title="The app’s best guess at what this amount is. Change it if it’s wrong — once set, it counts toward your case total.">
      <select
        value={current}
        onChange={(e) => onChange(e.target.value as LedgerBucket)}
        className={confirmed ? "confirmed" : "unconfirmed"}
      >
        {BUCKETS.map((b) => (
          <option key={b} value={b}>
            {BUCKET_LABELS[b]}
          </option>
        ))}
      </select>
      {!confirmed && <span className="ledger-bucket-ai">AI</span>}
    </label>
  );
}

// ---------------------------------------------------------------------------
// 3. Costs-by-context table lives in CostsByContext.tsx — a by-type breakdown
// (subtotal + count + traceable line items) over the SAME totals object, net
// gated by verify-or-suppress. It replaced the old flat case-total block.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// 4. Costs vs coverage — the demoted comparison (was "actual vs. limit").
// Renders only when the asserted total has BOTH a cost subtotal and a
// coverage-term subtotal to compare.
// ---------------------------------------------------------------------------

function CostsVsCoverage({
  totals,
}: {
  totals: CaseTotals;
}) {
  if (!totals.totals) return null;
  const cost = totals.totals.bucketSubtotals.find((b) => b.bucket === "cost");
  const coverage = totals.totals.bucketSubtotals.find(
    (b) => b.bucket === "coverage_term",
  );
  if (!cost || !coverage) return null;
  const o = computeOverage(cost.cents, coverage.cents);
  return (
    <div className="ledger-costs-coverage">
      <h4 className="overview-subsection-title">Costs vs. coverage term</h4>
      <div className={`ledger-overage-result ${o.over ? "over" : "under"}`}>
        Costs {formatCents(cost.cents)} vs. coverage term{" "}
        {formatCents(coverage.cents)} ={" "}
        <strong>
          {o.gapCents === 0
            ? "exactly at the coverage term"
            : o.over
              ? `${formatCents(o.gapCents)} over`
              : `${formatCents(o.underByCents)} under`}
        </strong>{" "}
        <VerifiedMark what="Computed gap" />
      </div>
    </div>
  );
}

function CompletenessCallout({
  gaps,
  onNavigateToRecord,
}: {
  gaps: ReturnType<typeof scanForExtractionGaps>;
  onNavigateToRecord?: (recordId: string) => void;
}) {
  return (
    <details className="ledger-completeness">
      <summary>
        {gaps.length} record{gaps.length === 1 ? "" : "s"} mention money or dates
        the app didn’t capture
      </summary>
      <p className="overview-muted">
        These mention a dollar amount or date that didn’t become a saved fact, so
        it’s not in your totals or timeline. Worth a look if a recent one matters
        — not required. Nothing is changed automatically.
      </p>
      <ul>
        {gaps.map((g) => (
          <li key={g.record_id}>
            <button
              type="button"
              className="fact-source-link"
              onClick={() => onNavigateToRecord?.(g.record_id)}
            >
              {g.subject || "(untitled record)"}
            </button>{" "}
            <span className="overview-muted">
              {g.moneyGap && `amounts: ${g.moneyCandidates.join(", ")}`}
              {g.moneyGap && g.dateGap && " · "}
              {g.dateGap && `dates: ${g.dateCandidates.join(", ")}`}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}
